import { useMemo } from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import {
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  ComposedChart,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { fmtPct } from '@/lib/format-utils'
import { useRecoveryStore } from '@/stores/recovery-store'
import Loader from '@/components/zthd/Loader'

const DailyMetrics = () => {
  const { pid, country, startDate, endDate } = useRecoveryStore()

  // Build the API id
  const id = useMemo(() => `total_${country}_${pid}`, [country, pid])

  // Fetch data for each date (batch request)
  const { data: batchData, isLoading } = useSWR(
    startDate && endDate
      ? [`/troas/${id}/cumulative/batch`, { startDate, endDate }]
      : null,
    { revalidateOnFocus: false },
  )

  // Calculate metrics for each date from the fetched data
  const dailyMetrics = useMemo(() => {
    if (!batchData?.data) return []

    return Object.entries(batchData.data)
      .map(([date, dayData]) => {
        const roasObj = dayData?.roas || {}
        const roasArray = Object.keys(roasObj)
          .filter((key) => key.startsWith('day'))
          .map((key) => ({
            day: parseInt(key.replace('day', '')),
            roas: roasObj[key].roas,
          }))
          .sort((a, b) => a.day - b.day)

        // 数据天数（有多少天的回收数据）
        const dataDays = roasArray.length

        if (roasArray.length === 0) {
          return {
            date,
            dateLabel: dayjs(date).format('MM/DD'),
            dataDays: 0,
            breakevenDay: null,
            day30Roas: null,
            day90Roas: null,
            peakRoas: null,
            peakDay: null,
          }
        }

        // 回本日（首次达到100%的天数）
        const breakevenDay = roasArray.find((d) => d.roas >= 100)?.day ?? null

        // D30 ROAS
        const day30Roas = roasArray.find((d) => d.day === 30)?.roas ?? null

        // D90 ROAS
        const day90Roas = roasArray.find((d) => d.day === 90)?.roas ?? null

        // Peak ROAS
        const peakRoas = Math.max(...roasArray.map((d) => d.roas))
        const peakDay = roasArray.find((d) => d.roas === peakRoas)?.day ?? null

        return {
          date,
          dateLabel: dayjs(date).format('MM/DD'),
          dataDays,
          breakevenDay,
          day30Roas,
          day90Roas,
          peakRoas,
          peakDay,
        }
      })
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [batchData])

  // Y axis max
  const roasYMax = useMemo(() => {
    if (!dailyMetrics.length) return 200
    const values = dailyMetrics
      .flatMap((d) => [d.day30Roas, d.day90Roas, d.peakRoas])
      .filter((v) => v !== null)
    if (values.length === 0) return 200
    const maxValue = Math.max(...values)
    return Math.ceil(maxValue / 20) * 20
  }, [dailyMetrics])

  if (isLoading) {
    return <Loader>加载每日指标数据中...</Loader>
  }

  // If no batch data, show message about API requirement
  if (!batchData?.data) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-8 text-center text-amber-700">
        <p className="font-medium">需要后端支持批量查询接口</p>
        <p className="mt-2 text-sm">
          请确保 <code>/troas/{'{id}'}/cumulative/batch</code> 接口可用， 传入
          dates 数组参数，返回每天的回收数据。
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">每日关键指标趋势</CardTitle>
          <p className="text-sm text-slate-500">
            横坐标: 安装日期 ({dayjs(startDate).format('YYYY-MM-DD')} ~{' '}
            {dayjs(endDate).format('YYYY-MM-DD')}) | 纵坐标:
            该日安装用户的ROAS指标
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={dailyMetrics}>
              <XAxis
                dataKey="dateLabel"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                label={{
                  value: '安装日期',
                  position: 'insideBottom',
                  offset: -5,
                  fontSize: 12,
                  fill: '#64748b',
                }}
              />
              <YAxis
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                domain={[0, roasYMax]}
                tickFormatter={(value) => fmtPct(value)}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <div className="space-y-1">
                          <p className="font-semibold">
                            {dayjs(data.date).format('YYYY-MM-DD')}
                          </p>
                          <hr className="my-1" />
                          <p className="text-sm text-slate-500">
                            数据天数: {data.dataDays} 天
                          </p>
                          <p className="text-sm text-slate-500">
                            回本日:{' '}
                            {data.breakevenDay !== null
                              ? `Day ${data.breakevenDay}`
                              : '未达成'}
                          </p>
                          <p className="text-sm text-emerald-600">
                            D30 ROAS:{' '}
                            {data.day30Roas !== null
                              ? fmtPct(data.day30Roas)
                              : '-'}
                          </p>
                          <p className="text-sm text-purple-600">
                            D90 ROAS:{' '}
                            {data.day90Roas !== null
                              ? fmtPct(data.day90Roas)
                              : '-'}
                          </p>
                          <p className="text-sm text-amber-600">
                            峰值 ROAS:{' '}
                            {data.peakRoas !== null
                              ? `${fmtPct(data.peakRoas)} (Day ${data.peakDay})`
                              : '-'}
                          </p>
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Legend />

              {/* 100% breakeven line */}
              <ReferenceLine
                y={100}
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
              />

              <Line
                type="monotone"
                dataKey="day30Roas"
                name="D30 ROAS"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="day90Roas"
                name="D90 ROAS"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="peakRoas"
                name="峰值 ROAS"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">每日指标明细</CardTitle>
          <p className="text-sm text-slate-500">每天安装用户的关键回收指标</p>
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>日期</TableHead>
                  <TableHead className="text-right">数据天数</TableHead>
                  <TableHead className="text-right">回本日</TableHead>
                  <TableHead className="text-right text-emerald-600">
                    D30 ROAS
                  </TableHead>
                  <TableHead className="text-right text-purple-600">
                    D90 ROAS
                  </TableHead>
                  <TableHead className="text-right text-amber-600">
                    峰值 ROAS
                  </TableHead>
                  <TableHead className="text-right">峰值日</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dailyMetrics.map((item) => (
                  <TableRow key={item.date}>
                    <TableCell className="font-medium">
                      {dayjs(item.date).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell className="text-right text-slate-500">
                      {item.dataDays} 天
                    </TableCell>
                    <TableCell className="text-right">
                      {item.breakevenDay !== null ? (
                        <span className="text-green-600">
                          Day {item.breakevenDay}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right ${
                        item.day30Roas !== null && item.day30Roas >= 100
                          ? 'font-medium text-green-600'
                          : 'text-emerald-600'
                      }`}
                    >
                      {item.day30Roas !== null ? fmtPct(item.day30Roas) : '-'}
                    </TableCell>
                    <TableCell
                      className={`text-right ${
                        item.day90Roas !== null && item.day90Roas >= 100
                          ? 'font-medium text-green-600'
                          : 'text-purple-600'
                      }`}
                    >
                      {item.day90Roas !== null ? fmtPct(item.day90Roas) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-amber-600 font-medium">
                      {item.peakRoas !== null ? fmtPct(item.peakRoas) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-slate-500">
                      {item.peakDay !== null ? `Day ${item.peakDay}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default DailyMetrics
